import NoDataSvg from '@delmonicos/shared-design-system-core/assets/delmonicos-illustrations/svg/classic-light-placeholder-socket-1-168x168.svg';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Button } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { DatePickerInput } from '@delmonicos/shared-design-system-react/src/components/date-picker-input/date-picker-input';
import {
  DatePickerRangeOnSelectHandler,
  ModeEnum,
  coerceDatePickerRangeRequiredSelection,
} from '@delmonicos/shared-design-system-react/src/components/date-picker/date-picker';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Skeleton } from '@delmonicos/shared-design-system-react/src/components/skeleton/skeleton';
import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Toggle } from '@delmonicos/shared-design-system-react/src/components/toggle/toggle';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { GetChargePointOcppLogsModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-point-ocpp-logs.model';
import { OcppMessageType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/get-charge-point-ocpp-logs.model';
import { MessageType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/enums/message-type.enum';
import { OcppLogMessageFormatted } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/logger/ocpp-logs-messsages.interface';
import {
  DateArg,
  endOfToday,
  formatISO,
  isEqual,
  isToday,
  startOfToday,
  subDays,
} from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LogCardProvider,
  useLogCardContext,
} from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-card.context';
import { ByChannelFilterComponent } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-filters/by-channel-filter.component';
import { ByMethodFilterComponent } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-filters/by-method-filter.component';
import { ByPhysicalSocketFilterComponent } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-filters/by-physical-socket-filter.component';
import { ByTypeFilterComponent } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-filters/by-type-filter.component';
import { LogListComponent } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-list/log-list.component';
import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';
import { useCpmsBoGetChargePointLogsQuery } from '@service/cpms-bo-charge-point.service';

import * as css from './log-card.module.scss';

interface CallCardProps {
  chargePointId: string;
}

const LogCardContent = ({
  chargePointId,
}: CallCardProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { lastRecordedActivityDate } = useChargePointContext();
  const { isExpertMode, setIsExpertMode } = useLogCardContext();
  const [pollingInterval, setPollingInterval] = useState(0);
  const start = useMemo(
    () =>
      lastRecordedActivityDate
        ? subDays(lastRecordedActivityDate, 1)
        : startOfToday(),
    [lastRecordedActivityDate],
  );
  const end = useMemo(
    () => lastRecordedActivityDate ?? endOfToday(),
    [lastRecordedActivityDate],
  );
  const [parameters, setParameters] = useState<
    Omit<GetChargePointOcppLogsModel, 'ocppMessageMethod'> & {
      ocppMessageMethod: MessageType[];
    }
  >({
    start: formatISO(start),
    end: formatISO(end),
    direction: 'backward',
    ocppMessageType: [OcppMessageType.callResult, OcppMessageType.callResult],
    ocppMessageMethod: Object.values(MessageType),
  });

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isFilterRefetch, setIsFilterRefetch] = useState(false);

  const [logs, setLogs] = useState<OcppLogMessageFormatted[]>([]);
  const [offset, setOffset] = useState<DateArg<Date> | undefined>(undefined);
  const { data, isLoading, isFetching, refetch } =
    useCpmsBoGetChargePointLogsQuery(
      {
        chargePointId,
        ...parameters,
        offset,
      },
      {
        skip: !chargePointId,
        pollingInterval,
      },
    );

  useEffect(() => {
    if (!isFetching && data?.docs) {
      setLogs((prev) => {
        // Mode pagination : on charge des pages -> on append
        if (offset !== undefined) {
          return [...prev, ...data.docs];
        }

        // Mode live : remplace complètement, sinon duplication
        return data.docs;
      });
      setIsFilterRefetch(false);
    }
  }, [data, isFetching, offset]);

  const handleDateSelection: DatePickerRangeOnSelectHandler = (
    selected,
    id,
    modifier,
    e,
  ) => {
    if (!selected) {
      return;
    }

    const { from, to } = coerceDatePickerRangeRequiredSelection(
      selected,
      id,
      modifier,
      e,
    );

    if (!from || !to) {
      return;
    }

    if (
      isEqual(formatISO(from), parameters.start) &&
      isEqual(formatISO(to), parameters.end)
    ) {
      return;
    }
    setIsFilterRefetch(true);
    setLogs([]);
    setOffset(undefined);
    setParameters({
      ...parameters,
      start: formatISO(from),
      end: formatISO(to),
    });
  };

  const handleChangeParameters = (
    newParams: Omit<GetChargePointOcppLogsModel, 'ocppMessageMethod'> & {
      ocppMessageMethod: MessageType[];
    },
  ) => {
    setIsFilterRefetch(true);
    setLogs([]);
    setOffset(undefined);
    setParameters(newParams);
  };

  const onSeeMore = () => {
    if (data?.nextOffset) {
      setOffset(data.nextOffset);
    }
  };

  const handleRefresh = () => {
    if (
      pollingInterval === 0 &&
      isToday(parameters.end) &&
      offset === undefined
    ) {
      refetch();
      setPollingInterval(5 * 1000);
    } else {
      setPollingInterval(0);
    }
  };

  return (
    <>
      <Card
        direction="column"
        gap={GapEnum.M}
        className={css.container}
        flex={1}>
        <Stack
          gap={GapEnum.S}
          alignItems="center"
          justifyContent="space-between">
          <Button onClick={handleRefresh} className={css.refreshButton}>
            <Stack gap={GapEnum.S} alignItems="center">
              <Icon name="refresh-cw-01" />
              {pollingInterval === 0
                ? t('$.charge-point-details.maintenance.log.polling.activate')
                : t(
                    '$.charge-point-details.maintenance.log.polling.deactivate',
                  )}
            </Stack>
          </Button>
          <Stack flex={1} gap={GapEnum.S} alignItems="center">
            <Icon name="code-02" />
            <Text>
              {t('$.charge-point-details.maintenance.log.expert-mode')}
            </Text>

            <Toggle
              id="expert-mode-toggle"
              label=""
              checked={isExpertMode}
              onChange={() => setIsExpertMode(!isExpertMode)}
            />
          </Stack>
          <Stack gap={GapEnum.M}>
            <DatePickerInput
              mode={ModeEnum.Range}
              selected={{
                from: new Date(parameters.start),
                to: new Date(parameters.end),
              }}
              onSelect={handleDateSelection}
            />
            <ButtonIcon
              icon="filter-funnel-01"
              onClick={() => setIsFilterOpen(true)}
            />
          </Stack>
        </Stack>
        <Divider />
        {(isLoading || isFilterRefetch) && (
          <Stack direction="column" gap={GapEnum.S}>
            <Skeleton width="50%" />
            <Skeleton />
            <Skeleton />
            <Skeleton width="50%" />
            <Skeleton />
            <Skeleton />
          </Stack>
        )}
        {!isLoading && !isFilterRefetch && isFetching && (
          <Stack justifyContent="center">
            <Spinner />
          </Stack>
        )}

        {!isLoading && !isFilterRefetch && logs.length === 0 && (
          <Stack direction="column" alignItems="center" gap={GapEnum.M}>
            <NoDataSvg />
            {t('$.charge-point-details.maintenance.no-logs')}
          </Stack>
        )}

        {!isFilterRefetch && logs.length > 0 && (
          <LogListComponent
            logs={logs}
            isFetching={isFetching}
            isLoading={isLoading}
            nextOffset={data?.nextOffset}
            onSeeMore={onSeeMore}
          />
        )}
      </Card>
      <Dialog
        onClose={() => setIsFilterOpen(false)}
        open={isFilterOpen}
        position={DialogPosition.RIGHT}>
        <Stack direction="column" flex={1} className={css.filtersDialog}>
          <Dialog.Header
            icon="filter-funnel-01"
            title={t('$.charge-point-details.maintenance.filter.title')}
            onClose={() => setIsFilterOpen(false)}
          />
          <Stack
            gap={GapEnum.L}
            flex={1}
            direction="column"
            className={css.filtersContainer}>
            <ByTypeFilterComponent
              selectedOcppMessageType={
                parameters.ocppMessageType ?? [
                  OcppMessageType.callResult,
                  OcppMessageType.callResult,
                ]
              }
              handleSelectedOcppMessageType={(ocppMessageType) =>
                handleChangeParameters({ ...parameters, ocppMessageType })
              }
            />
            <Divider />
            <ByMethodFilterComponent
              selectedMethods={parameters.ocppMessageMethod ?? []}
              handleMethodsChange={(methods) =>
                handleChangeParameters({
                  ...parameters,
                  ocppMessageMethod: methods,
                })
              }
            />
            <Divider />
            <ByChannelFilterComponent
              channelId={parameters.channelId}
              handleChangeChannelId={(channelId) =>
                handleChangeParameters({ ...parameters, channelId })
              }
            />
            <Divider />
            <ByPhysicalSocketFilterComponent
              physicalSocketId={parameters.physicalSocketId}
              handleChangePhysicalSocketId={(physicalSocketId) =>
                handleChangeParameters({ ...parameters, physicalSocketId })
              }
            />
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
};

export const LogCard = ({
  chargePointId,
}: CallCardProps): React.JSX.Element => {
  return (
    <LogCardProvider>
      <LogCardContent chargePointId={chargePointId} />
    </LogCardProvider>
  );
};
